/**
 * "Plan limits in the app" (design 10 · Website & pricing, k9qobd): what the three limit states show, as a pure model
 * (tests/plans.test.mts). Copy is the design's:
 *
 *   1 Widget library   features outside your plan show a lock and the plan that has them; selecting one says
 *                      "<Widget> is on the <Plan> plan", what it does, and "Compare plans" (/pricing).
 *   2 Monthly limit    Brief me: "Briefs this month  20 of 20", "You’ve used this month’s briefs. They reset on Nov 1.",
 *                      "Add 20 briefs · $49" for owners and admins ("Ask an admin" for everyone else) and "Not now".
 *   3 Seat limit       Access → Invite: "All 10 seats are in use", "Add a seat for $25 a month, or remove someone first.",
 *                      "Add a seat and invite" and "Manage members".
 *
 * The numbers come from the Worker (worker/src/plan-limits.js: GET /api/dashboard/plan, the 403 limit answers and the
 * Access snapshot's `seats`); the plans and prices from auth/plans.ts.
 */
import { mayGrant, type FeatureKey } from "../auth/features";
import { planForGroup, planWith, usd, type PlanDef } from "../auth/plans";
import { can, type FeatureMap, type OrgRole, type UserGroup } from "../nav/navConfig";
import { WIDGETS, type WidgetDef } from "../overview/widgets/registry";

/** Where "Compare plans" and the buy buttons go (no checkout is designed: the existing contact address). */
export const COMPARE_PLANS = "/pricing";
export const CONTACT_SALES = "mailto:hello@chicagopolitics.org";

/** The design's icon and line for widgets it draws locked; others use the widget's own icon and catalog line. */
const LOCKED_COPY: Partial<Record<string, { icon: string; text: string }>> = {
  targetList: { icon: "target", text: "Track who you’ve met, scheduled and still need to contact on each item." },
  coalitions: { icon: "handshake", text: "" },
};

export interface LockedWidget { type: string; title: string; icon: string; plan: PlanDef; text: string }

/**
 * Library widgets this team can't add because its plan doesn't have them, each with the plan that does. Team groups
 * only (residents aren't on a team plan); a privateData feature the group can never have is left out (layer 4).
 */
export function lockedWidgets(group: UserGroup, role: OrgRole | null, features?: FeatureMap | null): LockedWidget[] {
  if (group !== "office" && group !== "advocacy") return [];
  const mine = planForGroup(group);
  return Object.values(WIDGETS).flatMap((d: WidgetDef) => {
    if (d.inLibrary === false || !d.feature || (d.audience !== "office" && d.audience !== "advocacy")) return [];
    if (can(group, role, d.feature, features) || !mayGrant(d.feature, group)) return [];
    const plan = planWith(d.feature as FeatureKey, mine);
    if (!plan) return [];
    const copy = LOCKED_COPY[d.type];
    return [{ type: d.type, title: d.title, icon: copy?.icon ?? d.icon, plan, text: copy?.text || d.description }];
  });
}

export const lockTitle = (w: LockedWidget) => `${w.title} is on the ${w.plan.name} plan`;

// ---------- Brief me: the monthly limit

export interface BriefAllowance { used: number; limit: number | null; resetsOn: string; addon: { count: number; price: number } | null }

/** "Nov 1" for "2026-11-01". */
export const shortDate = (ymd: string) => new Date(`${ymd}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

export const briefsReached = (a: BriefAllowance | null | undefined): a is BriefAllowance & { limit: number } =>
  !!a && a.limit != null && a.used >= a.limit;

export function briefLimitCopy(a: BriefAllowance & { limit: number }, role: OrgRole | null) {
  const admin = role === "owner" || role === "admin";
  return {
    title: "Brief me",
    meterLabel: "Briefs this month",
    meterValue: `${Math.min(a.used, a.limit)} of ${a.limit}`,
    fill: a.limit > 0 ? Math.min(1, a.used / a.limit) : 1,
    text: `You’ve used this month’s briefs. They reset on ${shortDate(a.resetsOn)}.`,
    primary: admin ? (a.addon ? `Add ${a.addon.count} briefs · ${usd(a.addon.price)}` : null) : "Ask an admin",
    canBuy: admin && !!a.addon,
    secondary: "Not now",
  };
}

// ---------- Access: the seat limit

export interface SeatInfo { used: number; limit: number | null; extraMonthly: number | null }

export const seatsFull = (s: SeatInfo | null | undefined): s is SeatInfo & { limit: number } => !!s && s.limit != null && s.used >= s.limit;

export function seatLimitCopy(s: SeatInfo & { limit: number }, orgName: string) {
  return {
    title: `Invite to ${orgName}`,
    banner: `All ${s.limit} seats are in use`,
    text: s.extraMonthly != null ? `Add a seat for ${usd(s.extraMonthly)} a month, or remove someone first.` : null,
    primary: "Add a seat and invite",
    secondary: "Manage members",
  };
}

/** A Worker answer that is a plan limit: 403 { error: { code: "limit_reached" | "seat_limit" }, briefs? | seats? }. */
export function limitAnswer(body: unknown): { code: "limit_reached"; briefs: BriefAllowance } | { code: "seat_limit"; seats: SeatInfo } | null {
  const b = body as { error?: { code?: string }; briefs?: BriefAllowance; seats?: SeatInfo } | null;
  if (b?.error?.code === "limit_reached" && b.briefs) return { code: "limit_reached", briefs: b.briefs };
  if (b?.error?.code === "seat_limit" && b.seats) return { code: "seat_limit", seats: b.seats };
  return null;
}
